import { Component, Input, OnDestroy } from "@angular/core";
import { combineLatest, Subject, Subscription, takeUntil } from "rxjs";
import { GasprecioService } from "../../../gasprecio/services/gasprecio.service";
import { SharedService } from "../../../gasprecio/services/shared.service";
import {
  Router,
  Event as NavigationEvent,
  NavigationEnd,
} from "@angular/router";
import Highcharts from "highcharts";
import type {
  Chart as HighchartsChart,
  ChartCallbackFunction as HighchartsChartCallback
} from 'highcharts';
import { GroupIds } from "src/app/modules/gasprecio/enums/groups-ids.enum";
import moment from "moment";

@Component({
  selector: 'app-heat-map-by-month-liters-amount',
  templateUrl: './heat-map-by-month-liters-amount.component.html',
  styleUrls: ['./heat-map-by-month-liters-amount.component.scss']
})
export class HeatMapByMonthLitersAmountComponent implements OnDestroy {

  @Input() station: any = false;

  private litersRefs:  HighchartsChart[] = [];
  private amountRefs:  HighchartsChart[] = [];
  chartCallbackLitersFactory(i: number): HighchartsChartCallback {
    return (c) => { this.litersRefs[i] = c; };
  }
  chartCallbackAmountFactory(i: number): HighchartsChartCallback {
    return (c) => { this.amountRefs[i] = c; };
  }
  changeZone = false;
  colorsMap = {
    regular: '#159546',
    premium: '#ea0000',
    diesel: '#1e2228'
  }
  optionsByRow: { liters: Highcharts.Options; amount: Highcharts.Options }[] = [];
  data = [];
  date = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
  event$!: Subscription;
  Highcharts = Highcharts;
  hours = [];
  idGroup: GroupIds | string = GroupIds.Pa;
  loading = false;
  private destroy$ = new Subject<void>();
  productsData = [];
  rol;
  route;
  stationCondition;
  stationId = '';
  variable = "liters";
  zone = '6178671b46818a57510e9218';
  first = true;
  loads = 0;
  constructor(
    private gasprecioService: GasprecioService,
    private sharedService: SharedService,
    private router: Router
  ) {
    this.rol = localStorage.getItem("rol");
    this.idGroup = this.rol === "Dios" ? localStorage.getItem("group") || GroupIds.Pa : localStorage.getItem("group");
    this.stationId = localStorage.getItem('idStation');
    this.event$ = this.router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationEnd) {
        this.route = /[^/]*$/.exec(event.url)[0];
      }
    });

    combineLatest([
      this.sharedService.sharedGroup,
      this.sharedService.sharedZoneHeatMap,
      this.sharedService.sharedStation,
      this.sharedService.sharedSelectedMonth,
      this.sharedService.sharedVariableMode
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([idGroup, zone, stationId, period, mode]) => {
        if (idGroup !== this.idGroup) {
          this.idGroup = idGroup;
        }

        if (this.zone !== zone) {
          this.zone = zone;
        }

        if (this.stationId !== stationId) {
          if(stationId != ''){
            this.stationId = stationId;
          }
        }

        if (period !== this.date) {
          this.date = period;
        }

        if (mode !== this.variable) {
          this.variable = mode;
          console.log(this.variable)
        }

        if (!this.loading) {
          this.getHeatMapMonthly();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getHeatMapMonthly() {
    this.first = false;
    this.loading = true;
    this.data = [];
    this.hours = [];
    this.loads ++;

    setTimeout(() => {
      this.stationCondition = this.stationId && this.stationId != 'zone';
      let url: any;
      url = this.gasprecioService.getHeatMapMonthlyByStationModes(this.idGroup, this.stationCondition ? this.stationId : this.zone, this.date, this.stationCondition);
      url.subscribe({
        next: (data: any) => {
          data = data.data
          this.productsData = this.generateTableData(data);
          const maxLiters  = Math.max(1, ...this.productsData.map(p => p.liters  ?? 0));
          const maxAmount  = Math.max(1, ...this.productsData.map(p => p.amount  ?? 0));
          this.litersRefs = [];
          this.amountRefs = [];

          this.optionsByRow = this.productsData.map(p => ({
            liters: this.setData(p.liters, { unit: 'lts', color: p.color, height: 30, max: maxLiters}),
            amount: this.setData(p.amount, { unit: '$',   color: p.color, height: 30, max: maxAmount })
          }));
        },
        complete: () => {
          this.loading = false;
        }
      });
    }, 2000);
  }

  generateTableData(data): any[] {
    const order = ['regular', 'premium', 'diesel', 'total'];

    const formatData = order.map(key => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      liters: data.liters[key],
      amount: data.amount[key],
      color: this.colorsMap[key]
    }));
    return formatData;
  }

  setData = (
    value: number | null | undefined,
    opts: {
      unit: 'lts' | '$',
      color?: string,
      height?: number,
      decimals?: number,
      max: number
    },
  ): Highcharts.Options => {
    const v = Number(value ?? 0);
    const color = opts.color ?? '#96D6FF';
    const height = opts.height ?? 30;
    const decimals = Number.isFinite(opts.decimals) ? (opts.decimals as number) : 0;
    const yMax = Math.max(1, opts.max ?? v);
    const fmt = (n: number) => Highcharts.numberFormat(n, decimals, '.', ',');
    const labelText = (n: number) => (opts.unit === '$' ? `$ ${fmt(n)}` : `${fmt(n)} lts`);

    return {
      chart: { type: 'bar', height, spacing: [0, 0, 0, 0] },
      title: { text: '' },
      credits: { enabled: false },
      exporting: { enabled: false },
      navigation: { buttonOptions: { enabled: false }},
      legend: { enabled: false },
      xAxis: { categories: [''], visible: false },
      yAxis: { min: 0, max: yMax, visible: false },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const y = (this as any).y as number;
          return `<b>${labelText(y)}</b>`;
        }
      },
      plotOptions: {
        series: { borderWidth: 0 },
        bar: {
          pointWidth: 27,
          dataLabels: {
            enabled: false
          }
        }
      },
      series: [{
        type: 'bar',
        data: [v],
        color,
        dataLabels: {
          enabled: v > 0,
          formatter: function () {
            const y = (this as any).y as number;
            return labelText(y);
          },
          style: {
            textOutline: '1px #fff',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#000000'
          },
          align: 'right',
          inside: false,
          x: 5,
          crop: false
        }
      }]
    };
  };
  private async chartToPng(chart: HighchartsChart, scale = 2, fallbackOptions?: Highcharts.Options): Promise<string> {
    const w = 200, h = 80;
    const exportOverrides: Highcharts.Options = {
      plotOptions: {
        series: {
          dataLabels: {
            style: { fontSize: '24px', fontWeight: '600' }
          }
        }
      }
    };

    let svg: string;

    try {
      if (!chart || (chart as any).destroyed) throw new Error('dead');
      const opts = Highcharts.merge(
        { exporting: { sourceWidth: w, sourceHeight: h, scale } },
        exportOverrides
      ) as Highcharts.Options;
      svg = chart.getSVG(opts);

    } catch {
      if (!fallbackOptions) throw new Error('Chart no inicializado y sin fallbackOptions');

      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.left = '-99999px';
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
      document.body.appendChild(el);

      try {
        const tmpOpts = Highcharts.merge(
          {},
          fallbackOptions,
          exportOverrides,
          { chart: { width: w, height: h, animation: false },
            exporting: { sourceWidth: w, sourceHeight: h, scale },
            credits: { enabled: false } } as Highcharts.Options
        );
        const tmp = Highcharts.chart(el, tmpOpts);
        svg = tmp.getSVG({ exporting: { sourceWidth: w, sourceHeight: h, scale } });
        tmp.destroy();
      } finally {
        el.remove();
      }
    }
    const svgUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; img.src = svgUrl; });
    const canvas = document.createElement('canvas');
    canvas.width = img.width || 1200 * scale;
    canvas.height = img.height || 600 * scale;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  }

  async toDataURLLitersAt(idx: number, scale = 2) {
    const c = this.litersRefs[idx];
    if (!c) throw new Error(`Chart liters idx ${idx} no inicializado`);
    return this.chartToPng(c, scale, this.optionsByRow[idx]?.liters);
  }
  async toDataURLAmountAt(idx: number, scale = 2) {
    const c = this.amountRefs[idx];
    if (!c) throw new Error(`Chart amount idx ${idx} no inicializado`);
    return this.chartToPng(c, scale, this.optionsByRow[idx]?.amount);
  }

  async toDataURLAll(scale = 2) {
    const liters = await Promise.all(
      this.litersRefs.map((c, i) => this.chartToPng(c, scale, this.optionsByRow[i]?.liters))
    );
    const amount = await Promise.all(
      this.amountRefs.map((c, i) => this.chartToPng(c, scale, this.optionsByRow[i]?.amount))
    );
    return { liters, amount };
  }
}